import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Loader2,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Save,
  DollarSign,
  User,
  CreditCard,
  Shield,
  Heart,
  AlertTriangle,
  Calendar,
  FileText,
  Globe,
  GraduationCap,
  UserCog,
  UserPlus,
  Target,
  CheckCircle2,
  Clock,
  BookOpen,
  Construction,
  Pencil,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Progress } from "../components/ui/progress";
import { EmployeePayStubsTab } from "../components/employee/EmployeePayStubsTab";
import ForeignWorkerTab from "../components/employee/ForeignWorkerTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeDetail = () => {
  const { employeeId: id } = useParams();
  const navigate = useNavigate();
  const { isOfficeManager, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [sensitiveInfo, setSensitiveInfo] = useState(null);
  const [payrollSettings, setPayrollSettings] = useState(null);
  const [trainingAssignments, setTrainingAssignments] = useState([]);
  const [onboardingTasks, setOnboardingTasks] = useState([]);
  const [perfReviews, setPerfReviews] = useState([]);
  const [perfGoals, setPerfGoals] = useState([]);
  const [benefitEnrollments, setBenefitEnrollments] = useState([]);
  // Phase 14 — Pension
  const [pensionPlans, setPensionPlans] = useState([]);
  const [pensionEnrollments, setPensionEnrollments] = useState([]);
  const [pensionDialogOpen, setPensionDialogOpen] = useState(false);
  const [pensionEnrollmentForm, setPensionEnrollmentForm] = useState({
    plan_id: "",
    contribution_pct: "",
    beneficiary: "",
    enrollment_date: new Date().toISOString().split("T")[0],
    notes: "",
    status: "active",
  });
  const [activeTab, setActiveTab] = useState("profile");

  // Training assignment dialog state
  const [assignTrainingOpen, setAssignTrainingOpen] = useState(false);
  const [trainingPrograms, setTrainingPrograms] = useState([]);
  const [assignProgramsLoading, setAssignProgramsLoading] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    program_id: "",
    due_date: "",
    notes: "",
    send_notification: true,
  });
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  
  // Visibility toggles for sensitive fields
  const [showSIN, setShowSIN] = useState(false);
  const [showBanking, setShowBanking] = useState(false);
  
  // Dialog states
  const [garnishmentDialogOpen, setGarnishmentDialogOpen] = useState(false);
  const [emergencyContactDialogOpen, setEmergencyContactDialogOpen] = useState(false);
  
  // Form states
  const [newGarnishment, setNewGarnishment] = useState({
    type: "child_support",
    creditor_name: "",
    case_number: "",
    amount_per_pay: 0,
    total_owed: 0,
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
    notes: ""
  });
  
  const [newEmergencyContact, setNewEmergencyContact] = useState({
    name: "",
    relationship: "",
    phone: "",
    email: "",
    is_primary: false
  });

  // Phase 19.7 — Pay Stubs tab
  const [payStubs, setPayStubs] = useState([]);
  const [payStubsLoaded, setPayStubsLoaded] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchEmployeeData();
  }, [id]);

  // Phase 19.7: lazy-load pay stubs when the Pay Stubs tab is opened
  useEffect(() => {
    if (activeTab !== "paystubs" || payStubsLoaded || !id) return;
    const loadStubs = async () => {
      try {
        const res = await axios.get(`${API}/payroll/employees/${id}/pay-stubs`, { headers });
        setPayStubs(res.data?.pay_stubs || []);
      } catch (e) {
        setPayStubs([]);
      } finally {
        setPayStubsLoaded(true);
      }
    };
    loadStubs();
  }, [activeTab, id, payStubsLoaded]);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      // Wave 1: core employee data (must succeed)
      const [empRes, sensitiveRes, payrollRes, trainingRes] = await Promise.all([
        axios.get(`${API}/employees/${id}`, { headers }),
        axios.get(`${API}/employee-info/${id}`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/payroll/employees/${id}/settings`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/training/assignments?employee_id=${id}`, { headers }).catch(() => ({ data: { assignments: [] } })),
      ]);

      setEmployee(empRes.data);
      setSensitiveInfo(sensitiveRes.data || {});
      setPayrollSettings(payrollRes.data || {});
      setTrainingAssignments(trainingRes.data?.assignments || []);

      // Wave 2: HR module data (independent of core) - fire after core to avoid pool starvation
      const [onbRes, revRes, goalRes, enrRes] = await Promise.all([
        axios.get(`${API}/onboarding/tasks?employee_id=${id}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/performance/reviews?employee_id=${id}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/performance/goals?employee_id=${id}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/benefits/enrollments?employee_id=${id}`, { headers }).catch(() => ({ data: [] })),
      ]);
      setOnboardingTasks(onbRes.data || []);
      setPerfReviews(revRes.data || []);
      setPerfGoals(goalRes.data || []);
      setBenefitEnrollments(enrRes.data || []);
      // Phase 14 — Pension data
      try {
        const [planRes, pensionEnrollRes] = await Promise.all([
          axios.get(`${API}/pensions/plans?active_only=true`, { headers }),
          axios.get(`${API}/pensions/enrollments?employee_id=${id}`, { headers }),
        ]);
        setPensionPlans(planRes.data || []);
        setPensionEnrollments(pensionEnrollRes.data || []);
      } catch {
        // Pension module is optional; never block detail page load
      }
    } catch (error) {
      toast.error("Failed to load employee data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBasicInfo = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/employees/${id}`, employee, { headers });
      toast.success("Employee information saved");
    } catch (error) {
      toast.error("Failed to save employee information");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSensitiveInfo = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/employee-info/${id}`, sensitiveInfo, { headers });
      toast.success("Sensitive information saved");
    } catch (error) {
      toast.error("Failed to save sensitive information");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayrollSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/payroll/employees/${id}/settings`, payrollSettings, { headers });
      toast.success("Payroll settings saved");
    } catch (error) {
      toast.error("Failed to save payroll settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddGarnishment = async () => {
    try {
      const res = await axios.post(`${API}/employee-info/${id}/garnishments`, newGarnishment, { headers });
      setSensitiveInfo(prev => ({
        ...prev,
        garnishments: [...(prev.garnishments || []), res.data]
      }));
      setGarnishmentDialogOpen(false);
      setNewGarnishment({
        type: "child_support",
        creditor_name: "",
        case_number: "",
        amount_per_pay: 0,
        total_owed: 0,
        start_date: new Date().toISOString().split('T')[0],
        end_date: "",
        notes: ""
      });
      toast.success("Garnishment added");
    } catch (error) {
      toast.error("Failed to add garnishment");
    }
  };

  const handleDeleteGarnishment = async (garnishmentId) => {
    try {
      await axios.delete(`${API}/employee-info/${id}/garnishments/${garnishmentId}`, { headers });
      setSensitiveInfo(prev => ({
        ...prev,
        garnishments: prev.garnishments.filter(g => g.id !== garnishmentId)
      }));
      toast.success("Garnishment removed");
    } catch (error) {
      toast.error("Failed to remove garnishment");
    }
  };

  const handleAddEmergencyContact = async () => {
    try {
      const res = await axios.post(`${API}/employee-info/${id}/emergency-contacts`, newEmergencyContact, { headers });
      setSensitiveInfo(prev => ({
        ...prev,
        emergency_contacts: [...(prev.emergency_contacts || []), res.data]
      }));
      setEmergencyContactDialogOpen(false);
      setNewEmergencyContact({
        name: "",
        relationship: "",
        phone: "",
        email: "",
        is_primary: false
      });
      toast.success("Emergency contact added");
    } catch (error) {
      toast.error("Failed to add emergency contact");
    }
  };

  const handleDeleteEmergencyContact = async (contactId) => {
    try {
      await axios.delete(`${API}/employee-info/${id}/emergency-contacts/${contactId}`, { headers });
      setSensitiveInfo(prev => ({
        ...prev,
        emergency_contacts: prev.emergency_contacts.filter(c => c.id !== contactId)
      }));
      toast.success("Emergency contact removed");
    } catch (error) {
      toast.error("Failed to remove emergency contact");
    }
  };

  const openAssignTrainingDialog = async () => {
    setAssignTrainingOpen(true);
    setAssignProgramsLoading(true);
    setNewAssignment({ program_id: "", due_date: "", notes: "", send_notification: true });
    try {
      const res = await axios.get(`${API}/training/programs?active_only=true`, { headers });
      setTrainingPrograms(res.data?.programs || []);
    } catch (error) {
      toast.error("Failed to load training programs");
    } finally {
      setAssignProgramsLoading(false);
    }
  };

  const handleCreateAssignment = async () => {
    if (!newAssignment.program_id) {
      toast.error("Please select a training program");
      return;
    }
    try {
      setCreatingAssignment(true);
      const payload = {
        program_id: newAssignment.program_id,
        employee_ids: [id],
        due_date: newAssignment.due_date || null,
        notes: newAssignment.notes || null,
        send_notification: newAssignment.send_notification,
      };
      const res = await axios.post(`${API}/training/assignments`, payload, { headers });
      const created = res.data?.assignments || [];
      if (created.length === 0) {
        toast.warning("No assignment was created (employee may already have it)");
      } else {
        toast.success("Training assigned");
        setTrainingAssignments((prev) => [...created, ...prev]);
      }
      setAssignTrainingOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign training");
    } finally {
      setCreatingAssignment(false);
    }
  };

  const handleCreatePensionEnrollment = async () => {
    if (!pensionEnrollmentForm.plan_id) {
      toast.error("Please select a pension plan");
      return;
    }
    try {
      const payload = {
        employee_id: id,
        plan_id: pensionEnrollmentForm.plan_id,
        enrollment_date: pensionEnrollmentForm.enrollment_date || null,
        contribution_pct: pensionEnrollmentForm.contribution_pct
          ? parseFloat(pensionEnrollmentForm.contribution_pct)
          : null,
        beneficiary: pensionEnrollmentForm.beneficiary || null,
        notes: pensionEnrollmentForm.notes || null,
        status: pensionEnrollmentForm.status,
      };
      const res = await axios.post(`${API}/pensions/enrollments`, payload, { headers });
      // Re-hydrate from server so plan_name etc fields are populated
      const refreshed = await axios.get(`${API}/pensions/enrollments?employee_id=${id}`, { headers });
      setPensionEnrollments(refreshed.data || [res.data]);
      toast.success("Pension enrollment created");
      setPensionDialogOpen(false);
      setPensionEnrollmentForm({
        plan_id: "",
        contribution_pct: "",
        beneficiary: "",
        enrollment_date: new Date().toISOString().split("T")[0],
        notes: "",
        status: "active",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enroll in pension plan");
    }
  };

  const maskValue = (value, showFull) => {
    if (!value) return "Not set";
    if (showFull) return value;
    return "•".repeat(Math.min(value.length, 12));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="p-6">
        <p>Employee not found</p>
        <Button onClick={() => navigate("/employees")} className="mt-4">
          Back to Employees
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="employee-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/employees")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{employee.name}</h1>
          <p className="text-muted-foreground">{employee.position || "Employee"}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Badge variant={employee.status === "active" ? "default" : "secondary"}>
            {employee.status}
          </Badge>
          {isOfficeManager && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setActiveTab("profile");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              data-testid="employee-detail-edit-btn"
              title="Edit profile"
              className="h-8"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="profile" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <User className="h-4 w-4 hidden sm:block" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="portal" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <UserCog className="h-4 w-4 hidden sm:block" />
            Portal
          </TabsTrigger>
          <TabsTrigger value="onboarding" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <UserPlus className="h-4 w-4 hidden sm:block" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="training" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <GraduationCap className="h-4 w-4 hidden sm:block" />
            Training
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Target className="h-4 w-4 hidden sm:block" />
            Performance
          </TabsTrigger>
          <TabsTrigger value="payroll" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <DollarSign className="h-4 w-4 hidden sm:block" />
            Payroll
          </TabsTrigger>
          <TabsTrigger value="paystubs" className="flex-1 min-w-[80px] flex items-center justify-center gap-1" data-testid="tab-paystubs">
            <FileText className="h-4 w-4 hidden sm:block" />
            Pay Stubs
          </TabsTrigger>
          <TabsTrigger value="benefits" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Heart className="h-4 w-4 hidden sm:block" />
            Benefits
          </TabsTrigger>
          <TabsTrigger value="pension" className="flex-1 min-w-[80px] flex items-center justify-center gap-1" data-testid="tab-pension">
            <DollarSign className="h-4 w-4 hidden sm:block" />
            Pension
          </TabsTrigger>
          <TabsTrigger value="sensitive" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Shield className="h-4 w-4 hidden sm:block" />
            Sensitive
          </TabsTrigger>
          <TabsTrigger value="banking" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <CreditCard className="h-4 w-4 hidden sm:block" />
            Banking
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <FileText className="h-4 w-4 hidden sm:block" />
            Docs
          </TabsTrigger>
          <TabsTrigger value="foreign_worker" className="flex-1 min-w-[80px] flex items-center justify-center gap-1" data-testid="tab-foreign-worker">
            <Globe className="h-4 w-4 hidden sm:block" />
            Foreign Worker
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input
                  value={employee.name || ""}
                  onChange={(e) => setEmployee({ ...employee, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={employee.email || ""}
                  onChange={(e) => setEmployee({ ...employee, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={employee.phone || ""}
                  onChange={(e) => setEmployee({ ...employee, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Position</Label>
                <Input
                  value={employee.position || ""}
                  onChange={(e) => setEmployee({ ...employee, position: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={employee.department || ""}
                  onChange={(e) => setEmployee({ ...employee, department: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hire Date</Label>
                <Input
                  type="date"
                  value={employee.hire_date || ""}
                  onChange={(e) => setEmployee({ ...employee, hire_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={employee.address || ""}
                  onChange={(e) => setEmployee({ ...employee, address: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={employee.status}
                  onValueChange={(value) => setEmployee({ ...employee, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={employee.notes || ""}
                  onChange={(e) => setEmployee({ ...employee, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contacts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Emergency Contacts</CardTitle>
              <Button size="sm" onClick={() => setEmergencyContactDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add Contact
              </Button>
            </CardHeader>
            <CardContent>
              {(sensitiveInfo?.emergency_contacts || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No emergency contacts added</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Relationship</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Primary</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sensitiveInfo.emergency_contacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell>{contact.name}</TableCell>
                        <TableCell>{contact.relationship}</TableCell>
                        <TableCell>{contact.phone}</TableCell>
                        <TableCell>{contact.email || "-"}</TableCell>
                        <TableCell>{contact.is_primary ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteEmergencyContact(contact.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveBasicInfo} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Profile
            </Button>
          </div>
        </TabsContent>

        {/* Portal Management Tab */}
        <TabsContent value="portal" className="space-y-4" data-testid="tab-portal">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="mx-auto mb-4 p-5 rounded-full bg-amber-500/10 w-fit">
                <Construction className="h-10 w-10 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Portal Management</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Configure portal access, login credentials, and self-service settings for {employee.name}. Coming soon!
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onboarding Tab */}
        <TabsContent value="onboarding" className="space-y-4" data-testid="tab-onboarding">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Construction className="h-5 w-5 text-amber-500" />Onboarding Checklist</CardTitle>
              <CardDescription>Tasks for {employee.name}'s onboarding · <Link to="/onboarding" className="text-primary hover:underline">Manage in Onboarding module →</Link></CardDescription>
            </CardHeader>
            <CardContent>
              {onboardingTasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Construction className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No onboarding tasks assigned</p>
                  <p className="text-sm">Create tasks from the Onboarding module to track this employee's progress.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {onboardingTasks.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`emp-onboarding-${t.id}`}>
                      <div>
                        <p className="font-medium text-sm">{t.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{t.category}</Badge>
                          {t.due_date && <span className="text-xs text-muted-foreground">Due {new Date(t.due_date).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <Badge variant="outline" className={
                        t.status === "done" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" :
                        t.status === "in_progress" ? "bg-blue-500/10 text-blue-600 border-blue-500/30" :
                        "bg-slate-500/10 text-slate-600 border-slate-500/30"
                      }>{t.status.replace("_", " ")}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="space-y-4" data-testid="tab-training">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5" />
                    Training Assignments
                  </CardTitle>
                  <CardDescription>Training programs assigned to {employee.name}</CardDescription>
                </div>
                {isOfficeManager && (
                  <Button
                    size="sm"
                    onClick={openAssignTrainingDialog}
                    data-testid="assign-training-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Assign Training
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {trainingAssignments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GraduationCap className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No training assigned yet</p>
                  <p className="text-sm">Click "Assign Training" to assign a program to this employee.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {trainingAssignments.map((a) => {
                    const pct = a.total_materials > 0 ? Math.round((a.completed_materials / a.total_materials) * 100) : 0;
                    return (
                      <div key={a.id} className="flex items-center gap-4 p-4 rounded-lg border" data-testid={`emp-training-${a.id}`}>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium">{a.program_title}</h4>
                          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <Badge variant="outline" className={
                              a.status === "completed" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" :
                              a.status === "in_progress" ? "bg-blue-500/10 text-blue-600 border-blue-500/30" :
                              a.status === "overdue" ? "bg-red-500/10 text-red-600 border-red-500/30" :
                              ""
                            }>
                              {a.status?.replace("_", " ")}
                            </Badge>
                            {a.due_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Due: {a.due_date}</span>}
                            <span>{a.completed_materials || 0}/{a.total_materials || 0} materials</span>
                          </div>
                        </div>
                        <div className="w-32 flex items-center gap-2">
                          <Progress value={pct} className="flex-1" />
                          <span className="text-xs font-medium w-8 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance & Development Tab */}
        <TabsContent value="performance" className="space-y-4" data-testid="tab-performance">
          <Card>
            <CardHeader>
              <CardTitle>Performance Reviews</CardTitle>
              <CardDescription><Link to="/performance" className="text-primary hover:underline">Open Performance module →</Link></CardDescription>
            </CardHeader>
            <CardContent>
              {perfReviews.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No reviews recorded yet for {employee.name}.</p>
              ) : (
                <div className="space-y-2">
                  {perfReviews.map((r) => (
                    <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`emp-review-${r.id}`}>
                      <div>
                        <p className="font-medium text-sm">{r.period}</p>
                        {r.reviewer && <p className="text-xs text-muted-foreground">Reviewer: {r.reviewer}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        {r.overall_rating != null && <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">{r.overall_rating}/5</Badge>}
                        <Badge variant="outline" className={r.status === "finalized" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-slate-500/10 text-slate-600 border-slate-500/30"}>{r.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Development Goals</CardTitle>
            </CardHeader>
            <CardContent>
              {perfGoals.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No development goals yet.</p>
              ) : (
                <div className="space-y-2">
                  {perfGoals.map((g) => (
                    <div key={g.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`emp-goal-${g.id}`}>
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="font-medium text-sm">{g.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{g.category}</Badge>
                          {g.target_date && <span className="text-xs text-muted-foreground">Target {new Date(g.target_date).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <div className="w-32 flex items-center gap-2">
                        <Progress value={g.progress || 0} className="flex-1 h-2" />
                        <span className="text-xs w-10 text-right">{g.progress || 0}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pay Information</CardTitle>
              <CardDescription>Configure employee pay settings</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pay Type</Label>
                <Select
                  value={payrollSettings?.pay_type || "hourly"}
                  onValueChange={(value) => setPayrollSettings({ ...payrollSettings, pay_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="salary">Salary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>
                  {payrollSettings?.pay_type === "salary" ? "Annual Salary ($)" : "Hourly Rate ($)"}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={payrollSettings?.pay_type === "salary" 
                    ? (payrollSettings?.annual_salary || "") 
                    : (payrollSettings?.hourly_rate || "")}
                  onChange={(e) => setPayrollSettings({
                    ...payrollSettings,
                    [payrollSettings?.pay_type === "salary" ? "annual_salary" : "hourly_rate"]: parseFloat(e.target.value) || 0
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Tax Province</Label>
                <Select
                  value={payrollSettings?.tax_province || "British Columbia"}
                  onValueChange={(value) => setPayrollSettings({ ...payrollSettings, tax_province: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="British Columbia">British Columbia</SelectItem>
                    <SelectItem value="Alberta">Alberta</SelectItem>
                    <SelectItem value="Ontario">Ontario</SelectItem>
                    <SelectItem value="Quebec">Quebec</SelectItem>
                    <SelectItem value="Manitoba">Manitoba</SelectItem>
                    <SelectItem value="Saskatchewan">Saskatchewan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Additional Tax Deduction ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={payrollSettings?.additional_tax_fixed || ""}
                  onChange={(e) => setPayrollSettings({ ...payrollSettings, additional_tax_fixed: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Hours Tracking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="auto_pull"
                  checked={payrollSettings?.auto_pull_hours ?? true}
                  onCheckedChange={(checked) => setPayrollSettings({ ...payrollSettings, auto_pull_hours: checked })}
                />
                <Label htmlFor="auto_pull">Auto-pull hours from schedule</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                When enabled, hours will be automatically calculated from the employee's scheduled work. 
                You can still manually override individual entries during payroll processing.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Benefits & Deductions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="benefits"
                    checked={payrollSettings?.benefits_enrolled || false}
                    onCheckedChange={(checked) => setPayrollSettings({ ...payrollSettings, benefits_enrolled: checked })}
                  />
                  <Label htmlFor="benefits">Enrolled in Benefits</Label>
                </div>
                {payrollSettings?.benefits_enrolled && (
                  <div className="space-y-2 ml-6">
                    <Label>Benefits Deduction ($ per pay)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={payrollSettings?.benefits_deduction || ""}
                      onChange={(e) => setPayrollSettings({ ...payrollSettings, benefits_deduction: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="union"
                    checked={payrollSettings?.union_member || false}
                    onCheckedChange={(checked) => setPayrollSettings({ ...payrollSettings, union_member: checked })}
                  />
                  <Label htmlFor="union">Union Member</Label>
                </div>
                {payrollSettings?.union_member && (
                  <div className="space-y-2 ml-6">
                    <Label>Union Dues ($ per pay)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={payrollSettings?.union_dues || ""}
                      onChange={(e) => setPayrollSettings({ ...payrollSettings, union_dues: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSavePayrollSettings} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Payroll Settings
            </Button>
          </div>
        </TabsContent>

        {/* Phase 19.7 — Pay Stubs Tab */}
        <TabsContent value="paystubs" className="space-y-4" data-testid="tab-paystubs-content">
          <EmployeePayStubsTab payStubs={payStubs} payStubsLoaded={payStubsLoaded} />
        </TabsContent>

        {/* Sensitive Info Tab */}
        <TabsContent value="sensitive" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Social Insurance Number
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 space-y-2">
                  <Label>SIN</Label>
                  <div className="flex gap-2">
                    <Input
                      type={showSIN ? "text" : "password"}
                      value={sensitiveInfo?.sin || ""}
                      onChange={(e) => setSensitiveInfo({ ...sensitiveInfo, sin: e.target.value })}
                      placeholder="XXX-XXX-XXX"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowSIN(!showSIN)}
                    >
                      {showSIN ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Immigration Status</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Citizenship Status</Label>
                <Select
                  value={sensitiveInfo?.immigration?.citizenship_status || "citizen"}
                  onValueChange={(value) => setSensitiveInfo({
                    ...sensitiveInfo,
                    immigration: { ...sensitiveInfo?.immigration, citizenship_status: value }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="citizen">Canadian Citizen</SelectItem>
                    <SelectItem value="permanent_resident">Permanent Resident</SelectItem>
                    <SelectItem value="work_permit">Work Permit Holder</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Country of Citizenship</Label>
                <Input
                  value={sensitiveInfo?.immigration?.country_of_citizenship || ""}
                  onChange={(e) => setSensitiveInfo({
                    ...sensitiveInfo,
                    immigration: { ...sensitiveInfo?.immigration, country_of_citizenship: e.target.value }
                  })}
                />
              </div>
              {sensitiveInfo?.immigration?.citizenship_status === "work_permit" && (
                <>
                  <div className="space-y-2">
                    <Label>Work Permit Number</Label>
                    <Input
                      value={sensitiveInfo?.immigration?.work_permit_number || ""}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        immigration: { ...sensitiveInfo?.immigration, work_permit_number: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Permit Expiry</Label>
                    <Input
                      type="date"
                      value={sensitiveInfo?.immigration?.work_permit_expiry || ""}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        immigration: { ...sensitiveInfo?.immigration, work_permit_expiry: e.target.value }
                      })}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Garnishments */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Wage Garnishments
                </CardTitle>
                <CardDescription>Court-ordered deductions</CardDescription>
              </div>
              <Button size="sm" onClick={() => setGarnishmentDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add Garnishment
              </Button>
            </CardHeader>
            <CardContent>
              {(sensitiveInfo?.garnishments || []).length === 0 ? (
                <p className="text-muted-foreground text-sm">No garnishments recorded</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Creditor</TableHead>
                      <TableHead>Amount/Pay</TableHead>
                      <TableHead>Total Owed</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sensitiveInfo.garnishments.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell className="capitalize">{g.type?.replace(/_/g, " ")}</TableCell>
                        <TableCell>{g.creditor_name}</TableCell>
                        <TableCell>${g.amount_per_pay?.toFixed(2)}</TableCell>
                        <TableCell>{g.total_owed ? `$${g.total_owed.toFixed(2)}` : "-"}</TableCell>
                        <TableCell>{g.start_date}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteGarnishment(g.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveSensitiveInfo} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Sensitive Info
            </Button>
          </div>
        </TabsContent>

        {/* Banking Tab */}
        <TabsContent value="banking" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Direct Deposit Information
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBanking(!showBanking)}
                >
                  {showBanking ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                  {showBanking ? "Hide" : "Show"} Details
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="direct_deposit"
                  checked={sensitiveInfo?.direct_deposit ?? true}
                  onCheckedChange={(checked) => setSensitiveInfo({ ...sensitiveInfo, direct_deposit: checked })}
                />
                <Label htmlFor="direct_deposit">Enable Direct Deposit</Label>
              </div>
              
              {sensitiveInfo?.direct_deposit && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>Bank Name</Label>
                    <Input
                      value={showBanking ? (sensitiveInfo?.banking?.bank_name || "") : maskValue(sensitiveInfo?.banking?.bank_name, false)}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        banking: { ...sensitiveInfo?.banking, bank_name: e.target.value }
                      })}
                      disabled={!showBanking}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account Type</Label>
                    <Select
                      value={sensitiveInfo?.banking?.account_type || "chequing"}
                      onValueChange={(value) => setSensitiveInfo({
                        ...sensitiveInfo,
                        banking: { ...sensitiveInfo?.banking, account_type: value }
                      })}
                      disabled={!showBanking}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chequing">Chequing</SelectItem>
                        <SelectItem value="savings">Savings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Institution Number (3 digits)</Label>
                    <Input
                      type={showBanking ? "text" : "password"}
                      value={sensitiveInfo?.banking?.institution_number || ""}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        banking: { ...sensitiveInfo?.banking, institution_number: e.target.value }
                      })}
                      maxLength={3}
                      disabled={!showBanking}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Transit Number (5 digits)</Label>
                    <Input
                      type={showBanking ? "text" : "password"}
                      value={sensitiveInfo?.banking?.transit_number || ""}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        banking: { ...sensitiveInfo?.banking, transit_number: e.target.value }
                      })}
                      maxLength={5}
                      disabled={!showBanking}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Account Number</Label>
                    <Input
                      type={showBanking ? "text" : "password"}
                      value={sensitiveInfo?.banking?.account_number || ""}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        banking: { ...sensitiveInfo?.banking, account_number: e.target.value }
                      })}
                      disabled={!showBanking}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveSensitiveInfo} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Banking Info
            </Button>
          </div>
        </TabsContent>

        {/* Health Tab */}
        <TabsContent value="health" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Disability & Accommodations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_disability"
                  checked={sensitiveInfo?.disability?.has_disability || false}
                  onCheckedChange={(checked) => setSensitiveInfo({
                    ...sensitiveInfo,
                    disability: { ...sensitiveInfo?.disability, has_disability: checked }
                  })}
                />
                <Label htmlFor="has_disability">Employee has disability requiring accommodation</Label>
              </div>
              
              {sensitiveInfo?.disability?.has_disability && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>Disability Type</Label>
                    <Input
                      value={sensitiveInfo?.disability?.disability_type || ""}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        disability: { ...sensitiveInfo?.disability, disability_type: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Accommodations Required</Label>
                    <Input
                      value={sensitiveInfo?.disability?.accommodations_required || ""}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        disability: { ...sensitiveInfo?.disability, accommodations_required: e.target.value }
                      })}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Additional Notes</Label>
                    <Textarea
                      value={sensitiveInfo?.disability?.accommodation_notes || ""}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        disability: { ...sensitiveInfo?.disability, accommodation_notes: e.target.value }
                      })}
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Allergies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_allergies"
                  checked={sensitiveInfo?.allergies?.has_allergies || false}
                  onCheckedChange={(checked) => setSensitiveInfo({
                    ...sensitiveInfo,
                    allergies: { ...sensitiveInfo?.allergies, has_allergies: checked }
                  })}
                />
                <Label htmlFor="has_allergies">Employee has allergies</Label>
              </div>
              
              {sensitiveInfo?.allergies?.has_allergies && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Allergies (comma-separated)</Label>
                    <Input
                      value={(sensitiveInfo?.allergies?.allergies || []).join(", ")}
                      onChange={(e) => setSensitiveInfo({
                        ...sensitiveInfo,
                        allergies: { 
                          ...sensitiveInfo?.allergies, 
                          allergies: e.target.value.split(",").map(a => a.trim()).filter(a => a)
                        }
                      })}
                      placeholder="e.g., Peanuts, Latex, Bee stings"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Severity</Label>
                    <Select
                      value={sensitiveInfo?.allergies?.severity || "moderate"}
                      onValueChange={(value) => setSensitiveInfo({
                        ...sensitiveInfo,
                        allergies: { ...sensitiveInfo?.allergies, severity: value }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mild">Mild</SelectItem>
                        <SelectItem value="moderate">Moderate</SelectItem>
                        <SelectItem value="severe">Severe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="epipen"
                      checked={sensitiveInfo?.allergies?.epipen_required || false}
                      onCheckedChange={(checked) => setSensitiveInfo({
                        ...sensitiveInfo,
                        allergies: { ...sensitiveInfo?.allergies, epipen_required: checked }
                      })}
                    />
                    <Label htmlFor="epipen">EpiPen Required</Label>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveSensitiveInfo} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Health Info
            </Button>
          </div>
        </TabsContent>

        {/* Benefits Tab */}
        <TabsContent value="benefits" className="space-y-4" data-testid="tab-benefits">
          <Card>
            <CardHeader>
              <CardTitle>Benefit Enrollments</CardTitle>
              <CardDescription>{employee.name}'s enrolled plans · <Link to="/benefits" className="text-primary hover:underline">Manage in Benefits module →</Link></CardDescription>
            </CardHeader>
            <CardContent>
              {benefitEnrollments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Construction className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No active benefit enrollments</p>
                  <p className="text-sm">Enroll {employee.name} from the Benefits module.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {benefitEnrollments.map((e) => (
                    <div key={e.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`emp-benefit-${e.id}`}>
                      <div>
                        <p className="font-medium text-sm">{e.plan?.name || "Plan"}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {e.plan?.plan_type && <Badge variant="outline" className="text-xs">{e.plan.plan_type}</Badge>}
                          {e.effective_date && <span className="text-xs text-muted-foreground">Effective {new Date(e.effective_date).toLocaleDateString()}</span>}
                          {e.dependents > 0 && <span className="text-xs text-muted-foreground">{e.dependents} dependent{e.dependents !== 1 ? "s" : ""}</span>}
                        </div>
                      </div>
                      <Badge variant="outline" className={e.status === "active" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-slate-500/10 text-slate-600 border-slate-500/30"}>{e.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pension Tab — Phase 14 */}
        <TabsContent value="pension" className="space-y-4" data-testid="tab-pension-content">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Pension &amp; Retirement
                  </CardTitle>
                  <CardDescription>
                    {employee.name}'s pension enrollment.{" "}
                    <Link to="/pensions" className="text-primary hover:underline">Manage plans →</Link>
                  </CardDescription>
                </div>
                {isOfficeManager && (
                  <Button
                    size="sm"
                    onClick={() => setPensionDialogOpen(true)}
                    disabled={pensionPlans.length === 0}
                    data-testid="enroll-pension-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {pensionEnrollments.length > 0 ? "Add Enrollment" : "Enroll"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {pensionPlans.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <DollarSign className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No pension plans have been set up yet</p>
                  <p className="text-sm">
                    <Link to="/pensions" className="text-primary hover:underline">Create a plan in the Pensions module →</Link>
                  </p>
                </div>
              ) : pensionEnrollments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <DollarSign className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>{employee.name} is not enrolled in any pension plan</p>
                  {isOfficeManager && (
                    <p className="text-sm">Click "Enroll" above to set up retirement contributions.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {pensionEnrollments.map((e) => (
                    <div key={e.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`emp-pension-${e.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm">{e.plan_name || "Pension Plan"}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {e.plan_provider && <Badge variant="outline" className="text-xs">{e.plan_provider}</Badge>}
                          {e.contribution_pct != null && (
                            <span className="text-xs text-muted-foreground">
                              Contribution {e.contribution_pct}%
                            </span>
                          )}
                          {e.plan_employer_match_pct ? (
                            <span className="text-xs text-emerald-600">
                              + Employer match {e.plan_employer_match_pct}%
                            </span>
                          ) : null}
                          {e.enrollment_date && (
                            <span className="text-xs text-muted-foreground">
                              Enrolled {new Date(e.enrollment_date).toLocaleDateString()}
                            </span>
                          )}
                          {e.beneficiary && (
                            <span className="text-xs text-muted-foreground">Beneficiary: {e.beneficiary}</span>
                          )}
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={
                          e.status === "active"
                            ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                            : "bg-slate-500/10 text-slate-600 border-slate-500/30"
                        }
                      >
                        {e.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Employee Documents</CardTitle>
              <CardDescription>Certifications, contracts, and other documents</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Document management coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="foreign_worker" className="space-y-4">
          <ForeignWorkerTab employeeId={id} />
        </TabsContent>
      </Tabs>

      {/* Garnishment Dialog */}
      <Dialog open={garnishmentDialogOpen} onOpenChange={setGarnishmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Wage Garnishment</DialogTitle>
            <DialogDescription>Enter the garnishment details</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={newGarnishment.type}
                onValueChange={(value) => setNewGarnishment({ ...newGarnishment, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="child_support">Child Support</SelectItem>
                  <SelectItem value="spousal_support">Spousal Support</SelectItem>
                  <SelectItem value="tax_debt">Tax Debt</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Creditor Name</Label>
              <Input
                value={newGarnishment.creditor_name}
                onChange={(e) => setNewGarnishment({ ...newGarnishment, creditor_name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount Per Pay ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newGarnishment.amount_per_pay}
                  onChange={(e) => setNewGarnishment({ ...newGarnishment, amount_per_pay: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Total Owed ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newGarnishment.total_owed}
                  onChange={(e) => setNewGarnishment({ ...newGarnishment, total_owed: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={newGarnishment.start_date}
                onChange={(e) => setNewGarnishment({ ...newGarnishment, start_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGarnishmentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddGarnishment}>Add Garnishment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency Contact Dialog */}
      <Dialog open={emergencyContactDialogOpen} onOpenChange={setEmergencyContactDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Emergency Contact</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={newEmergencyContact.name}
                onChange={(e) => setNewEmergencyContact({ ...newEmergencyContact, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Relationship</Label>
              <Input
                value={newEmergencyContact.relationship}
                onChange={(e) => setNewEmergencyContact({ ...newEmergencyContact, relationship: e.target.value })}
                placeholder="e.g., Spouse, Parent, Sibling"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={newEmergencyContact.phone}
                onChange={(e) => setNewEmergencyContact({ ...newEmergencyContact, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                value={newEmergencyContact.email}
                onChange={(e) => setNewEmergencyContact({ ...newEmergencyContact, email: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_primary_contact"
                checked={newEmergencyContact.is_primary}
                onCheckedChange={(checked) => setNewEmergencyContact({ ...newEmergencyContact, is_primary: checked })}
              />
              <Label htmlFor="is_primary_contact">Primary Contact</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmergencyContactDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddEmergencyContact}>Add Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Training Dialog */}
      <Dialog open={assignTrainingOpen} onOpenChange={setAssignTrainingOpen}>
        <DialogContent data-testid="assign-training-dialog">
          <DialogHeader>
            <DialogTitle>Assign Training to {employee.name}</DialogTitle>
            <DialogDescription>
              Pick a training program and (optionally) set a due date and notes.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>Training Program *</Label>
              <Select
                value={newAssignment.program_id}
                onValueChange={(v) => setNewAssignment({ ...newAssignment, program_id: v })}
                disabled={assignProgramsLoading}
              >
                <SelectTrigger data-testid="assign-training-program-select">
                  <SelectValue placeholder={assignProgramsLoading ? "Loading..." : "Select a program"} />
                </SelectTrigger>
                <SelectContent>
                  {trainingPrograms.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No active programs available.
                    </div>
                  ) : (
                    trainingPrograms.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`assign-training-option-${p.id}`}>
                        {p.title}
                        {p.category ? ` · ${p.category}` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due Date (Optional)</Label>
              <Input
                type="date"
                value={newAssignment.due_date}
                onChange={(e) => setNewAssignment({ ...newAssignment, due_date: e.target.value })}
                data-testid="assign-training-due-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={newAssignment.notes}
                onChange={(e) => setNewAssignment({ ...newAssignment, notes: e.target.value })}
                placeholder="Instructions or context for this assignment..."
                rows={3}
                data-testid="assign-training-notes"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="send_notification"
                checked={newAssignment.send_notification}
                onCheckedChange={(checked) =>
                  setNewAssignment({ ...newAssignment, send_notification: !!checked })
                }
              />
              <Label htmlFor="send_notification">Send notification to employee</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTrainingOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateAssignment}
              disabled={creatingAssignment || !newAssignment.program_id}
              data-testid="assign-training-confirm-btn"
            >
              {creatingAssignment && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 14 — Pension Enrollment Dialog */}
      <Dialog open={pensionDialogOpen} onOpenChange={setPensionDialogOpen}>
        <DialogContent data-testid="pension-enrollment-dialog">
          <DialogHeader>
            <DialogTitle>Enroll {employee.name} in Pension Plan</DialogTitle>
            <DialogDescription>
              Pick a plan and configure their contribution percentage.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>Pension Plan *</Label>
              <Select
                value={pensionEnrollmentForm.plan_id}
                onValueChange={(v) => {
                  const plan = pensionPlans.find((p) => p.id === v);
                  setPensionEnrollmentForm({
                    ...pensionEnrollmentForm,
                    plan_id: v,
                    contribution_pct: plan?.employee_contribution_pct ?? "",
                  });
                }}
              >
                <SelectTrigger data-testid="pension-plan-select">
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {pensionPlans.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No plans configured. Create one in the Pensions module.
                    </div>
                  ) : (
                    pensionPlans.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`pension-plan-opt-${p.id}`}>
                        {p.name}
                        {p.provider ? ` · ${p.provider}` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Contribution % (Optional)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={pensionEnrollmentForm.contribution_pct}
                  onChange={(e) => setPensionEnrollmentForm({ ...pensionEnrollmentForm, contribution_pct: e.target.value })}
                  data-testid="pension-contribution-input"
                  placeholder="e.g. 5.0"
                />
              </div>
              <div className="space-y-2">
                <Label>Enrollment Date</Label>
                <Input
                  type="date"
                  value={pensionEnrollmentForm.enrollment_date}
                  onChange={(e) => setPensionEnrollmentForm({ ...pensionEnrollmentForm, enrollment_date: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Beneficiary (Optional)</Label>
              <Input
                value={pensionEnrollmentForm.beneficiary}
                onChange={(e) => setPensionEnrollmentForm({ ...pensionEnrollmentForm, beneficiary: e.target.value })}
                placeholder="Full name"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                rows={2}
                value={pensionEnrollmentForm.notes}
                onChange={(e) => setPensionEnrollmentForm({ ...pensionEnrollmentForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPensionDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreatePensionEnrollment}
              disabled={!pensionEnrollmentForm.plan_id}
              data-testid="pension-enrollment-confirm-btn"
            >
              Enroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDetail;
